import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Group,
  SimpleGrid,
  Paper,
  Text,
  Table,
  Stack,
  Title,
} from "@mantine/core";
import moment from "moment";
import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useQuizBasePath } from "../../hooks/useQuizBasePath";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useCellRender } from "../../hooks/useCellRender";
import QuizStatusBadge from "../../components/quiz/QuizStatusBadge";
import { McaEntityBanner } from "../../components/mca/McaEntityIndicators";
import { formatQuizTargetLicences } from "../../data/quizLicenses";
import { formatQuizTargetRoles } from "../../data/quizParticipants";

const apiEntity = "quiz";

const QuizDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tabNav = useTabNavigate();
  const basePath = useQuizBasePath();
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const [quiz, setQuiz] = useState(null);
  const [stats, setStats] = useState({});
  const [recentResults, setRecentResults] = useState([]);
  const [userRoles, setUserRoles] = useState([]);

  useEffect(() => {
    let active = true;
    const loadRoles = async () => {
      const res = await api.search({
        apiEntity: "userRole",
        searchQuery: {},
        currentPage: 1,
        pageSize: 200,
      });
      if (active) setUserRoles(res?.docs ?? []);
    };
    loadRoles();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      const [quiz, statsRes, resultsRes] = await Promise.all([
        api.getById({ apiEntity, id }),
        api.Quiz.getStatistics(id),
        api.Quiz.getResults(id),
      ]);
      setQuiz(quiz);
      setStats(statsRes?.data ?? {});
      const rows = (resultsRes?.data ?? [])
        .filter((r) => r.completedAt || r.result?.completedAt)
        .sort(
          (a, b) =>
            new Date(b.completedAt ?? b.result?.completedAt) -
            new Date(a.completedAt ?? a.result?.completedAt)
        )
        .slice(0, 10);
      setRecentResults(rows);
    };
    load();
  }, [id]);

  if (!quiz) {
    return (
      <Page title="Quiz Detail">
        <Text color="dimmed">Loading...</Text>
      </Page>
    );
  }

  const roleLabel = formatQuizTargetRoles(quiz.targetRole, userRoles);

  const handleClose = () => {
    if (tabNav.enabled) {
      tabNav.closeActive();
      return;
    }
    navigate(basePath);
  };

  return (
    <Page
      title={
        <Group spacing="sm" align="center">
          <Title order={2}>{quiz.title}</Title>
          <QuizStatusBadge status={quiz.status} />
        </Group>
      }
      actions={
        <Group>
          <Button onClick={() => navigate(`${basePath}/${id}/edit`)}>
            Edit Quiz
          </Button>
          <Button color="green" onClick={() => navigate(`${basePath}/${id}/results`)}>
            View Results
          </Button>
          <Button variant="default" onClick={handleClose}>
            Close
          </Button>
        </Group>
      }
    >
      <McaEntityBanner entity={quiz} />
      <SimpleGrid cols={4} mb="lg">
        {[
          { label: "Total Participants", value: stats.total ?? 0 },
          { label: "Completed", value: stats.completed ?? 0 },
          { label: "Pending", value: stats.pending ?? 0 },
          { label: "Pass Rate", value: `${stats.passRate ?? 0}%` },
        ].map((card) => (
          <Paper key={card.label} p="md" withBorder>
            <Text size="xs" color="dimmed">
              {card.label}
            </Text>
            <Text size="xl" weight={700}>
              {card.value}
            </Text>
          </Paper>
        ))}
      </SimpleGrid>

      <SimpleGrid cols={2} mb="lg">
        <Paper p="md" withBorder>
          <Title order={5} mb="sm">
            Quiz Information
          </Title>
          <Stack spacing="xs">
            <Text size="sm">Start: {quiz.startDate ? moment(quiz.startDate).format("LL") : "--"}</Text>
            <Text size="sm">Deadline: {quiz.deadline ? moment(quiz.deadline).format("LL") : "--"}</Text>
            <Text size="sm">Passing Rate: {quiz.passingRate}%</Text>
            <Text size="sm">Questions: {quiz.questions?.length ?? 0}</Text>
            <Text size="sm">Duration: {quiz.testDuration > 0 ? `${quiz.testDuration} min` : "Unlimited"}</Text>
            <Text size="sm">Max Retake Count: {quiz.maxRetakeCount ?? 100}</Text>
          </Stack>
        </Paper>

        <Paper p="md" withBorder>
          <Title order={5} mb="sm">
            Target Participants
          </Title>
          <Text size="sm">Role: {roleLabel}</Text>
          <Text size="sm" mt="xs">
            Licences: {formatQuizTargetLicences(quiz)}
          </Text>
        </Paper>
      </SimpleGrid>

      <Paper p="md" withBorder mb="lg">
        <Title order={5} mb="sm">
          Required Documents
        </Title>
        {!quiz.documents?.length && (
          <Text size="sm" color="dimmed">
            No documents uploaded
          </Text>
        )}
        {(quiz.documents ?? []).map((doc) => (
          <Text key={doc._id} size="sm">
            {doc.fileName}
          </Text>
        ))}
      </Paper>

      <Paper p="md" withBorder>
        <Title order={5} mb="sm">
          Recent Participants
        </Title>
        <Table striped>
          <thead>
            <tr>
              <th>Participant</th>
              <th>Status</th>
              <th>Try Count</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {recentResults.map((row) => (
              <tr key={row.user?._id}>
                <td>{cellRender.User(row.user)}</td>
                <td>
                  <QuizStatusBadge status={row.status} />
                </td>
                <td>
                  {row.attemptCount ?? 0} / {quiz.maxRetakeCount ?? 100}
                </td>
                <td>{row.score ?? "--"}</td>
              </tr>
            ))}
            {!recentResults.length && (
              <tr>
                <td colSpan={4}>
                  <Text color="dimmed" size="sm">
                    No completions yet
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Paper>
    </Page>
  );
};

export default QuizDetail;
